import { defineStore, acceptHMRUpdate } from 'pinia'
import { computed, ref } from 'vue'
import { useRegionStore } from './region'
import { usePatientStore } from './patient'

/**
 * The clinic day (Outpatient Phase 1).
 *
 * The day is held here, keyed by activity and view, so the navigation loop
 * (schedule -> chart -> back) can restore it without a refetch, together with
 * the per-activity UI state (`ui`: the provider filter, the calendar's view and date, and the scroll position).
 *
 * THE RULE THIS STORE EXISTS TO KEEP: nothing refetches the day after its own
 * write. The visit index lags the table by about a second, so a refetch right
 * after a status change would return the day WITHOUT the change the user just
 * made (the provider list shipped exactly that bug). Every write returns the
 * visit(s) it wrote and `applyVisits` merges them in. Reading the day again is
 * a user action - the Refresh button.
 */
// The keys an edit may set; everything else in the posted visit is what the read returned.
const EDITABLE_APPOINTMENT_KEYS = ['visit_type', 'appointment_type', 'start_timestamp', 'duration_minutes', 'provider_uid', 'reason_for_visit', 'notes', 'urgency', 'visit_mode']
const TEXT_KEYS = ['reason_for_visit', 'notes']
const NOT_THIS_PATIENTS = "This appointment belongs to the patient's template, not to this chart, so it cannot be edited here."
// A patient the student has not touched yet is a PLANNED row on `planned:<template patient uid>`: never a uid a
// write may use (the template's own is one, and writes to it have been a bug class of this feature).
export const PLANNED_PREFIX = 'planned:'
export const isPlannedUid = (uid) => typeof uid === 'string' && uid.startsWith(PLANNED_PREFIX)
const NOT_YET_OPENED = 'This student has not opened this patient yet, so there is nothing to change.'
const CLOSED_MEANWHILE = 'This appointment was rescheduled or closed while you were editing. Close this and open the current one.'

export const useClinicStore = defineStore('clinic', () => {
  // { [`${activity_uid}|${view}`]: day payload }
  const days = ref({})
  // { [activity_uid]: { provider_filter, calendar_view, calendar_day, scroll_top, show_removed? } }
  // show_removed (the template view's "Show removed") is absent until the user sets it, and absent means on.
  // calendar_view is day | week | month; calendar_day maps a view of the day (mine | template | ...) to the
  // day shown, as the calendar's own day number (absent until the user moves it: the calendar then opens on its home day).
  const ui = ref({})
  const current_key = ref(null)
  // { [dayKey]: true }: a day held that was left for a page that changes it (a walk-in was registered
  // elsewhere), so the next mount of the calendar reads it again, once. Cleared by the read.
  const stale = ref({})

  /** The most recently read or written day. */
  const day = computed(() => (current_key.value ? days.value[current_key.value] || null : null))

  function dayKey(activity_uid, view = 'mine') {
    return `${activity_uid}|${view}`
  }

  function dayFor(activity_uid, view = 'mine') {
    return days.value[dayKey(activity_uid, view)] || null
  }

  function uiFor(activity_uid) {
    if (!ui.value[activity_uid]) ui.value[activity_uid] = { provider_filter: null, calendar_view: 'day', calendar_day: {}, scroll_top: 0 }
    return ui.value[activity_uid]
  }

  async function api() {
    const regionStore = useRegionStore()
    return await regionStore.api()
  }

  // The region store's api() legitimately resolves to undefined when the
  // session has no organization context (see stores/provider.js, issue #822).
  function requireApi(apiInstance, action) {
    if (!apiInstance) {
      throw new Error(`Unable to ${action}: no organization context is available`)
    }
    return apiInstance
  }

  function store(activity_uid, view, payload) {
    const key = dayKey(activity_uid, view)
    days.value[key] = payload
    current_key.value = key
    delete stale.value[key]
    return payload
  }

  /** The held day is out of date: the calendar reads it once more the next time it mounts. */
  function markStale(activity_uid, view = 'mine') {
    stale.value[dayKey(activity_uid, view)] = true
  }
  const isStale = (activity_uid, view = 'mine') => !!stale.value[dayKey(activity_uid, view)]

  // The orders in a work queue, keyed like the day and the queue (Part 2: the Referrals queue; prescriptions: the To transmit
  // queue). Held beside the day, read on their own: a day read never carries them, and a failed orders read must not take
  // the held day with it. The queue defaults to `referral`, the first of them.
  // { [dayKey|queue]: payload }, { [dayKey|queue]: boolean }, { [dayKey|queue]: message }
  const orders = ref({})
  const ordersLoading = ref({})
  const ordersError = ref({})
  const ordersKey = (activity_uid, view, queue) => `${dayKey(activity_uid, view)}|${queue}`
  const ordersFor = (activity_uid, view = 'mine', queue = 'referral') => orders.value[ordersKey(activity_uid, view, queue)] || null
  const isOrdersLoading = (activity_uid, view = 'mine', queue = 'referral') => !!ordersLoading.value[ordersKey(activity_uid, view, queue)]
  const ordersErrorFor = (activity_uid, view = 'mine', queue = 'referral') => ordersError.value[ordersKey(activity_uid, view, queue)] || ''
  // What a failed read says, by what the queue lists.
  const ORDERS_ERRORS = {
    referral: 'The referrals could not be loaded.',
    transmit: 'The prescriptions to transmit could not be loaded.',
  }

  const dayPath = (course_uid, activity_uid) => `/course/${course_uid}/activity/${activity_uid}/clinic`

  /** Read the day. `view` is mine | template | student. */
  async function GetDay(course_uid, activity_uid, { view = 'mine', user_uid, simulation_run_uid } = {}) {
    const apiInstance = requireApi(await api(), 'load the Medical Office')
    const params = { view }
    if (user_uid) params.user_uid = user_uid
    if (simulation_run_uid) params.simulation_run_uid = simulation_run_uid
    const response = await apiInstance.get(`${dayPath(course_uid, activity_uid)}/day`, { params })
    if (response.status === 200) return store(activity_uid, view, response.data)
  }

  /**
   * Read the orders in a queue for the day's patients (`queue` is `referral`, the default, or `transmit`), with the parameters the day read takes.
   * The held payload is replaced only by a good answer; a failure keeps what was held, sets the error the page shows,
   * and is thrown to the caller as well.
   */
  async function GetOrders(course_uid, activity_uid, { view = 'mine', user_uid, simulation_run_uid, version, queue = 'referral' } = {}) {
    const key = ordersKey(activity_uid, view, queue)
    ordersLoading.value[key] = true
    try {
      const apiInstance = requireApi(await api(), 'load the orders')
      const params = { queue, view }
      if (user_uid) params.user_uid = user_uid
      if (simulation_run_uid) params.simulation_run_uid = simulation_run_uid
      if (version && version !== 'published') params.version = version
      const response = await apiInstance.get(`${dayPath(course_uid, activity_uid)}/orders`, { params })
      if (response.status !== 200) throw new Error('The orders could not be loaded.')
      orders.value[key] = response.data
      delete ordersError.value[key]
      return response.data
    } catch (e) {
      ordersError.value[key] = ORDERS_ERRORS[queue] || 'The orders could not be loaded.'
      throw e
    } finally {
      ordersLoading.value[key] = false
    }
  }

  /**
   * Save the body of ONE action taken on an order of a patient of the day (the body `buildActionBody` in
   * composables/orderActions.js makes) from a page with no chart open. The same request the patient store's `SaveData` sends,
   * to the patient the body names, which the day's orders read gave. Nothing is held: the caller reads the orders again.
   */
  async function SaveOrderAction(body) {
    const apiInstance = requireApi(await api(), 'save the order action')
    const response = await apiInstance.post(`/patient/${body.patient_id}/flowsheet/${body.flowsheet_uid}`, body)
    return response.data
  }

  /**
   * Start the caller's day. Deliberate and explicit: for an activity with
   * attempts, starting creates the attempt. The response IS the started day,
   * built from the table, so it is stored as-is and never re-read.
   */
  async function StartDay(course_uid, activity_uid) {
    const apiInstance = requireApi(await api(), 'start the Medical Office')
    const response = await apiInstance.post(`${dayPath(course_uid, activity_uid)}/start`, {
      timezone_offset: new Date().getTimezoneOffset(),
    })
    if (response.status === 200) return store(activity_uid, 'mine', response.data)
  }

  /**
   * Merge visits a write returned into a day, by patient_uid + uid. Visits of
   * different patients share uids (an instance keeps its template visit's uid),
   * so uid alone would let one patient's appointment overwrite another's.
   */
  function applyVisits(visits, key = current_key.value) {
    const target = key ? days.value[key] : null
    if (!target) return
    for (const visit of visits || []) {
      const at = target.visits.findIndex((v) => v.patient_uid === visit.patient_uid && v.uid === visit.uid)
      if (at !== -1) target.visits.splice(at, 1, visit)
      // A visit this day has no row for is added only when its patient is one of the day's: a write made from a
      // chart can return the visit of a patient this day does not hold, and it must not appear in it.
      else if ((target.patients || []).some((p) => p.uid === visit.patient_uid)) target.visits.push(visit)
    }
  }

  const sameVisit = (a, b) => a.patient_uid === b.patient_uid && a.uid === b.uid

  /**
   * Merge what a schedule write returned into the held template-view day, never reading it again. Patient rows are
   * added or replaced by uid FIRST (applyVisits only adds a visit whose patient is on the day), then visit rows by
   * patient_uid + uid; providers are merged and the roster replaced; `deleted` then removes the background
   * entries (their patient row and visit) and the activity's own appointments (the visit whose uid is the entry
   * uid). A deleted override removes nothing: the restored plain row came back in `visits`.
   */
  function applySchedule(data, key) {
    const target = key ? days.value[key] : null
    if (!target || !data) return
    for (const patient of data.patients || []) {
      const at = target.patients.findIndex((p) => p.uid === patient.uid)
      if (at !== -1) target.patients.splice(at, 1, patient)
      else target.patients.push(patient)
    }
    for (const visit of data.visits || []) {
      const at = target.visits.findIndex((v) => sameVisit(v, visit))
      if (at !== -1) target.visits.splice(at, 1, visit)
      else target.visits.push(visit)
    }
    if (data.providers) target.providers = { ...target.providers, ...data.providers }
    if (data.provider_schedules) target.provider_schedules = { ...target.provider_schedules, ...data.provider_schedules }
    if (data.provider_roster) target.provider_roster = [...data.provider_roster]
    const gone = data.deleted || {}
    // A row is named by its entry uid (`schedule.entry_uid`, which every sender uses), else by its own uid.
    const named = (set, v) => set.has(v.schedule?.entry_uid) || set.has(v.uid)
    const background = new Set(gone.background || [])
    if (background.size) {
      const rows = target.visits.filter((v) => v.schedule?.source === 'background' && named(background, v))
      const patients = new Set([...background, ...rows.map((v) => v.uid)].map((uid) => `background:${uid}`))
      for (const v of rows) patients.add(v.patient_uid)
      target.patients = target.patients.filter((p) => !patients.has(p.uid))
      target.visits = target.visits.filter((v) => !patients.has(v.patient_uid))
    }
    const entries = new Set(gone.appointments || [])
    if (entries.size) target.visits = target.visits.filter((v) => !(named(entries, v) && v.schedule?.source !== 'template'))
  }

  /**
   * The instructor's schedule: the ONE write the template view makes. Nothing of the instructor's schedule
   * ever goes to a patient. `course_uid` is the activity's course, or the string 'null' for a library
   * template activity. `body` is { background?, appointments?, overrides?, providers? }; the response is merged
   * into the held day (see applySchedule) and returned.
   */
  async function SaveSchedule(course_uid, activity_uid, body, { version } = {}) {
    const apiInstance = requireApi(await api(), 'save the schedule')
    const payload = { ...body }
    if (version && version !== 'published') payload.version = version
    const response = await apiInstance.post(`${dayPath(course_uid, activity_uid)}/schedule`, payload)
    if (response.status === 200) {
      applySchedule(response.data, dayKey(activity_uid, 'template'))
      return response.data
    }
  }

  // In-flight first touches, by activity and template patient: two actions on one planned patient share ONE request.
  const ensuring = {}

  /**
   * The first touch: ask the backend to copy a planned patient (idempotent) and merge what it answers into the held
   * day - the planned patient row and its planned appointments are replaced by the instance's, in place, so the
   * calendar shows no gap and no duplicate. Only the student's own day ('mine') can do this: a copy made while
   * reading another student's day would be the reader's. Returns { patient, visits, visit_uids }.
   */
  async function EnsurePatient(course_uid, activity_uid, template_patient_uid, { view = 'mine', version } = {}) {
    if (view !== 'mine') throw new Error(NOT_YET_OPENED)
    const slot = `${activity_uid}|${template_patient_uid}`
    if (!ensuring[slot]) {
      ensuring[slot] = (async () => {
        const apiInstance = requireApi(await api(), 'open the patient')
        const body = { template_patient_uid }
        if (version && version !== 'published') body.version = version
        const response = await apiInstance.post(`${dayPath(course_uid, activity_uid)}/patient`, body)
        if (response.status !== 200 || !response.data?.patient?.uid) throw new Error('The patient could not be opened.')
        applyEnsured(dayKey(activity_uid, view), response.data, template_patient_uid)
        return response.data
      })().finally(() => {
        delete ensuring[slot]
      })
    }
    return ensuring[slot]
  }

  /**
   * Replace a planned patient and its planned appointments in the held day by the instance's. A patient the day already
   * holds as an instance (a Group's copy the student had not opened) keeps its place and takes the answered row, which no
   * longer says `unopened`: the student's own link exists now.
   */
  function applyEnsured(key, data, template_patient_uid) {
    const target = key ? days.value[key] : null
    if (!target) return
    const planned = target.patients.find((p) => p.planned && p.template_patient_uid === template_patient_uid)
    if (planned) {
      target.patients = target.patients.map((p) => (p === planned ? data.patient : p))
      target.visits = target.visits.filter((v) => v.patient_uid !== planned.uid)
    } else if (target.patients.some((p) => p.uid === data.patient.uid)) {
      target.patients = target.patients.map((p) => (p.uid === data.patient.uid ? data.patient : p))
    } else {
      target.patients.push(data.patient)
    }
    applyVisits(data.visits, key)
  }

  /**
   * The patient and visit an action may WRITE to. An ordinary patient is itself. A planned one is first copied
   * (EnsurePatient) and the action goes to the INSTANCE and to the instance's row of the same appointment
   * (`visit_uids`); nothing is ever built from the planned uid, and a failed copy rejects before any write.
   */
  async function writable(patient_uid, visit_uid, context) {
    if (!isPlannedUid(patient_uid)) return { patient_uid, visit_uid }
    const template_uid = patient_uid.slice(PLANNED_PREFIX.length)
    const ensured = await EnsurePatient(context.course_uid, context.activity_uid, template_uid, {
      view: context.view || 'mine',
      version: context.version,
    })
    return { patient_uid: ensured.patient.uid, visit_uid: visit_uid ? ensured.visit_uids?.[visit_uid] || visit_uid : visit_uid, ensured }
  }

  /**
   * Move a live appointment to another status.
   * `context` = { course_uid, activity_uid, version?, view? }.
   */
  async function ChangeStatus(patient_uid, visit_uid, action_uid, context) {
    const apiInstance = requireApi(await api(), 'change the status')
    ;({ patient_uid, visit_uid } = await writable(patient_uid, visit_uid, context))
    const body = {
      action_uid,
      course_uid: context.course_uid,
      activity_uid: context.activity_uid,
    }
    // What was entered with the action, for the action's own fields (stored beside the status's log row).
    if (context.action_data) body.action_data = context.action_data
    if (context.version) body.version = context.version
    // Sent ONLY when a provider is chosen, exactly as SaveData does: "myself" is
    // the absence of the key, not `entered_as: <my uid>`.
    const entering_as = usePatientStore().entering_as
    if (entering_as?.uid) body.entered_as = entering_as.uid
    const response = await apiInstance.post(`/patient/${patient_uid}/visit/${visit_uid}/status`, body)
    if (response.status === 200) {
      const key = dayKey(context.activity_uid, context.view || 'mine')
      rememberProvider(context.provider, key)
      applyVisits([response.data.visit], key)
      return response.data.visit
    }
  }

  /**
   * Set the status of an appointment on TEMPLATE content - authoring, through
   * the ordinary visit save (no transition log). The visit may belong to a
   * template patient that is not the one loaded in the patient store, so this
   * reads and posts to the visit's own patient rather than going through SaveVisit.
   *
   * A day row is the brief dict from the search index: posting it would replace the
   * template visit's full `visit_type` with the brief embed and could write stale index
   * values over a fresher edit. So the visit is read by key first and only `status` and
   * `status_phase` are changed on that full object. A failed read posts nothing.
   * `status` = { label, phase }; `context` = { activity_uid, course_uid? } - the activity the
   * template is being authored through, which is what lets the backend read it for staff.
   */
  async function SetAuthoringStatus(visit, status, context = {}) {
    const apiInstance = requireApi(await api(), 'set the status')
    const params = context.activity_uid ? { activity_uid: context.activity_uid } : undefined
    if (params && context.course_uid) params.course_uid = context.course_uid
    const url = `/patient/${visit.patient_uid}/visit/${visit.uid}`
    const read = params ? await apiInstance.get(url, { params }) : await apiInstance.get(url)
    const current = read.status === 200 ? read.data?.visit : null
    if (!current) throw new Error('The appointment could not be read, so its status was not changed.')
    const response = await apiInstance.post(`/patient/${visit.patient_uid}/visit`, {
      visit: { ...current, status: status.label, status_phase: status.phase },
    })
    if (response.status === 200) {
      const saved = response.data.visit
      applyVisits([saved], dayKey(context.activity_uid, 'template'))
      return saved
    }
  }

  /**
   * A provider the user just picked may not be on the day yet, and the day resolves
   * names from its own `providers` map: without this a new appointment would read
   * "Unknown provider" until the day is read again.
   */
  function rememberProvider(embed, key) {
    const target = key ? days.value[key] : null
    if (target && embed?.uid && !target.providers[embed.uid]) target.providers[embed.uid] = embed
  }

  /** A provider's embed from any day held, for surfaces that have no day of their own. */
  function providerEmbed(uid) {
    if (!uid) return null
    for (const held of Object.values(days.value)) if (held?.providers?.[uid]) return held.providers[uid]
    return null
  }

  /**
   * Book an appointment (step one of the booking dialog). Authoring on a template
   * patient and a live booking on an instance are the same call; the backend tells
   * them apart. `context` = { course_uid, activity_uid, version?, view?, provider? } - the day
   * the visit is merged into, and what the backend needs to authorise a template.
   */
  async function BookAppointment(patient_uid, appointment, context) {
    const apiInstance = requireApi(await api(), 'book the appointment')
    ;({ patient_uid } = await writable(patient_uid, null, context))
    const body = { appointment, course_uid: context.course_uid, activity_uid: context.activity_uid }
    if (context.version) body.version = context.version
    const response = await apiInstance.post(`/patient/${patient_uid}/appointment`, body)
    if (response.status === 200) {
      const key = dayKey(context.activity_uid, context.view || 'mine')
      rememberProvider(context.provider, key)
      applyVisits([response.data.visit], key)
      return response.data.visit
    }
  }

  /**
   * Change an appointment's details after booking. `patient_uid` is the patient the caller is working on (the
   * chart's, or the day row's) and the only one ever read or written: a row of another patient - a template
   * appointment a student's chart reads through, which carries the TEMPLATE's patient_uid and read_only - is
   * refused before any request, so an edit made from an instance cannot rewrite the template's visit.
   * `edits` holds only the keys the user changed here (visit_type, appointment_type, start_timestamp,
   * duration_minutes, provider_uid, reason_for_visit, notes, urgency, visit_mode); nothing else is ever taken from the caller.
   *
   * Same read-then-post as SetAuthoringStatus, for the same reason: a day row is the brief dict from the
   * search index, and posting it would overwrite the stored visit_type with a stub. So the visit is read by
   * key and that full object is posted with only the edited keys altered. A failed read posts nothing.
   * The server writes only the keys that differ from the stored row, so status and the other server-owned
   * keys that came back from the read are echoes it ignores. A 409 (changed while saving) rejects with the
   * server's message. `context` = { course_uid, activity_uid, version?, view?, provider? }.
   */
  async function UpdateAppointment(patient_uid, visit, edits, context = {}) {
    if (!patient_uid || visit.patient_uid !== patient_uid || visit.read_only) throw new Error(NOT_THIS_PATIENTS)
    const apiInstance = requireApi(await api(), 'save the appointment')
    if (isPlannedUid(patient_uid)) {
      // Copy first, then edit the instance's own row of this appointment.
      const w = await writable(patient_uid, visit.uid, context)
      const own = w.ensured.visits.find((v) => v.uid === w.visit_uid)
      if (!own) throw new Error(CLOSED_MEANWHILE)
      patient_uid = w.patient_uid
      visit = own
    }
    const url = `/patient/${patient_uid}/visit/${visit.uid}`
    const params = {}
    if (context.activity_uid) params.activity_uid = context.activity_uid
    if (context.course_uid) params.course_uid = context.course_uid
    let current = null
    try {
      const read = Object.keys(params).length ? await apiInstance.get(url, { params }) : await apiInstance.get(url)
      current = read.status === 200 ? read.data?.visit : null
    } catch (e) {
      current = null
    }
    if (!current) throw new Error('The appointment could not be read, so it was not changed.')
    // Read back by this patient's key it can still be another patient's visit (the template's, read through).
    if (current.patient_uid !== patient_uid || current.read_only) throw new Error(NOT_THIS_PATIENTS)
    // Rescheduled or closed since the row was read: an edit would only land on the old appointment.
    if (current.rescheduled_to || current.status_phase === 'released') throw new Error(CLOSED_MEANWHILE)
    const next = { ...current }
    for (const key of EDITABLE_APPOINTMENT_KEYS) {
      if (!(key in edits)) continue
      // An empty text and an unset one are the same value: leave what was stored as it is.
      // (Spaces alone are empty.)
      const text = (v) => (v && String(v).trim() ? v : '')
      if (TEXT_KEYS.includes(key) && text(edits[key]) === text(current[key])) continue
      next[key] = edits[key]
    }
    // A visit type the dialog filled in by itself: only for a visit that has none, whatever the row it was opened with said.
    if (edits.visit_type_if_missing && !current.visit_type) next.visit_type = edits.visit_type_if_missing
    const body = { visit: next }
    if (context.version) body.version = context.version
    const response = await apiInstance.post(`/patient/${patient_uid}/visit`, body)
    if (response.status === 200) {
      const saved = response.data.visit
      const key = dayKey(context.activity_uid, context.view || 'mine')
      rememberProvider(context.provider, key)
      applyVisits([saved], key)
      return saved
    }
  }

  /**
   * Rebook an upcoming appointment: the old one is retired, the new one appears. Both
   * come back from the one request and both are applied here.
   * `fields` = { start_timestamp, provider_uid?, duration_minutes? }; `context.read_only` refuses with no request.
   */
  async function Reschedule(patient_uid, visit_uid, fields, context) {
    if (isPlannedUid(patient_uid) && !context.read_only) {
      ;({ patient_uid, visit_uid } = await writable(patient_uid, visit_uid, context))
      context = { ...context, working_patient_uid: patient_uid }
    }
    // `context.read_only`: the visit is the template's, read through an instance's chart. Never rescheduled from here.
    // `context.working_patient_uid`: the patient the caller works on; a visit of any other patient is refused too.
    const foreign = context.working_patient_uid && context.working_patient_uid !== patient_uid
    if (context.read_only || foreign) throw new Error("This appointment belongs to the patient's template and cannot be rescheduled from this chart.")
    const apiInstance = requireApi(await api(), 'reschedule the appointment')
    const body = { ...fields, course_uid: context.course_uid, activity_uid: context.activity_uid }
    if (context.version) body.version = context.version
    const response = await apiInstance.post(`/patient/${patient_uid}/visit/${visit_uid}/reschedule`, body)
    if (response.status === 200) {
      const { old_visit, new_visit } = response.data
      const key = dayKey(context.activity_uid, context.view || 'mine')
      rememberProvider(context.provider, key)
      applyVisits([old_visit, new_visit], key)
      return { old_visit, new_visit }
    }
  }

  return {
    days,
    ui,
    day,
    dayKey,
    providerEmbed,
    dayFor,
    uiFor,
    markStale,
    isStale,
    applySchedule,
    SaveSchedule,
    GetDay,
    orders,
    ordersFor,
    isOrdersLoading,
    ordersErrorFor,
    GetOrders,
    SaveOrderAction,
    StartDay,
    EnsurePatient,
    applyVisits,
    ChangeStatus,
    ResolveWritable: writable,
    SetAuthoringStatus,
    BookAppointment,
    UpdateAppointment,
    Reschedule,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useClinicStore, import.meta.hot))
}
